import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NavController, NavParams, Platform } from 'ionic-angular';
import { DailyLogOperationInstance, LogComment, TimeMaskOptions } from '../../../models/projects/operation.models';
import { OperationService } from '../../../services/operations/operation/operation.service';
import { DateService } from '../../../services/date/date.service';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { DateParserService } from '../../../services/daily-log/daily-log-time/date-parser-service';
import { TimeZoneService } from '../../../services/time-zone/time-zone.service';

@Component({
  selector: 'page-operation-generic-comment',
  templateUrl: 'operation-generic-comment.html',
})

export class OperationGenericCommentPage implements OnInit {
  @Input() isWeb: boolean;
  @Input() comment: LogComment = new LogComment();
  @Input() min: string;
  @Input() max: string;
  @Input() minDate: PtLuxonDate;
  @Input() maxDate: PtLuxonDate;
  @Input() multipleDayOperation: boolean;
  @Output() commentChange = new EventEmitter();
  @Output() timeChange = new EventEmitter();
  @Output() deleteRequest = new EventEmitter();

  operationInstance: DailyLogOperationInstance = new DailyLogOperationInstance();
  loading: boolean = false;

  public startTimeMask: Array<string | RegExp> = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public operationService: OperationService,
    public dateService: DateService,
    private dateParserService: DateParserService,
    public platform: Platform,
    private timeZoneService: TimeZoneService
  ) {
    this.operationInstance = navParams.get('operationInstance');
  }

  async ngOnInit() {
    this.min = '00:00';
    this.max = '23:59';

    if ((!this.comment.startTimeToShow || this.comment.startTimeToShow === '')
      && this.minDate && this.minDate.dateTime && this.comment.startTimeDate && this.minDate.dateTime > this.comment.startTimeDate.dateTime) {
      this.comment.startTimeDate = this.minDate;
    }
  }

  commentChanged(comment: any) {
    this.comment.data = comment ? comment.data : null;
    this.comment.appear = comment ? comment.appear : null;
    this.comment.milestone = comment ? comment.milestone : false;
    this.commentChange.emit(this.comment);
  }

  deleteRequested() {
    this.deleteRequest.emit(true);
  }

  removeStart() {
    this.comment.startTimeToShow = null;
    this.timeChange.emit(true);
  }

  async startChanged() {
    this.timeChange.emit(true);

    const allowed = await this.dateService.timeAllowed(this.comment.startTimeToShow,
      this.comment.startTimeDate && this.comment.startTimeDate.dateTime ? this.comment.startTimeDate.dateTime : this.minDate.dateTime);
    const valid = this.dateService.timeIsValid(this.comment.startTimeToShow);

    if (!allowed || !valid) {
      return;
    }

    this.comment.startTimeDate = await this.dateService.setPtLuxonDateFromUserEnteredString(
      this.comment.startTimeToShow,
      this.comment.startTimeDate && this.comment.startTimeDate.dateTime ? this.comment.startTimeDate.dateTime : this.minDate.dateTime);
    this.comment.startTimeOffset = this.comment.startTimeDate.dateTime && this.comment.startTimeDate.offsetNotNull() ? this.comment.startTimeDate.dateTime.offset / 60 : null;

    if (!this.comment.startTimeToShow) {
      return;
    }
  }

  // Gets called whenever the user changes a start time to configure the correct regex pattern.
  // Abandon all hope, ye who enter here.
  regexChange(timeEntered: string, startChanged: boolean) {
    if (this.timeNullOrEmpty(timeEntered)) {
      return;
    }

    const timeMask: Array<string | RegExp> = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];
    const digits = this.calculateEnteredDigits(timeEntered);

    timeMask[1] = this.configureTimeMask({
      minDigit: Number.parseInt(this.min.charAt(0).toString(), 10),
      maxDigit: Number.parseInt(this.max.charAt(0).toString(), 10),
      digitCombo: digits[0],
      totalMaxDigit: 9,
      position: 1,
    });

    timeMask[3] = this.configureTimeMask({
      minDigit: Number.parseInt(this.min.charAt(0).toString() + this.min.charAt(1).toString(), 10),
      maxDigit: Number.parseInt(this.max.charAt(0).toString() + this.max.charAt(1).toString(), 10),
      digitCombo: Number.parseInt(digits[0].toString() + digits[1].toString(), 10),
      totalMaxDigit: 5,
      position: 3,
    });

    timeMask[4] = this.configureTimeMask({
      minDigit: Number.parseInt(this.min.charAt(0).toString() + this.min.charAt(1).toString() + this.min.charAt(3).toString(), 10),
      maxDigit: Number.parseInt(this.max.charAt(0).toString() + this.max.charAt(1).toString() + this.max.charAt(3).toString(), 10),
      digitCombo: Number.parseInt(digits[0].toString() + digits[1].toString() + digits[2].toString(), 10),
      totalMaxDigit: 9,
      position: 4,
    });

    if (startChanged) {
      this.startTimeMask = timeMask;
    }
  }

  async startOffsetSelected(ptLuxonDate: PtLuxonDate) {
    this.comment.startTimeOffset = ptLuxonDate.dateTime.offset / 60;
  }

  private configureTimeMask(options: TimeMaskOptions): RegExp {

    if (options.minDigit === options.maxDigit) {
      return new RegExp(`[${this.min.charAt(options.position)}` + '-' + `${this.max.charAt(options.position)}]`);
    }

    if (options.minDigit === options.digitCombo) {
      return new RegExp(`[${this.min.charAt(options.position)}` + '-' + `${options.totalMaxDigit}]`);
    }

    if (options.maxDigit === options.digitCombo) {
      return new RegExp(`[0` + '-' + `${this.max.charAt(options.position)}]`);
    }

    return new RegExp(`[0-${options.totalMaxDigit}]`);
  }

  private calculateEnteredDigits(timeEnter: string): number[] {
    const numbers = [];
    numbers.push(Number.parseInt(timeEnter.charAt(0), 10));
    numbers.push(Number.parseInt(timeEnter.charAt(1), 10));
    numbers.push(Number.parseInt(timeEnter.charAt(3), 10));
    numbers.push(Number.parseInt(timeEnter.charAt(4), 10));
    return numbers;
  }

  private getDigit(time: string, arrayPosition: number): number {
    return Number(time.charAt(arrayPosition));
  }

  private timeNullOrEmpty(time: string): boolean {
    if (time === undefined || time === null || time === '') {
      return true;
    }

    return false;
  }
}



// WEBPACK FOOTER //
// ./src/pages/operations/operation-generic-comment/operation-generic-comment.ts